import { createContext, useEffect, useState } from "react";
import { BrowserRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AlertProvider } from "./contexts/AlertContext";
import AlertPopup from "./components/AlertPopup";
import { Ghg } from "./pages/Ghg";
import { Jobs } from "./pages/Jobs";
import { ValueAdded } from "./pages/ValueAdded";
import { Dashboard } from "./pages/Dashboard";
import { Upload } from "./pages/Upload";
import { Header } from "./components/Header";
import { DataProvider } from "./contexts/DataContext";
import { Box, Stack, ThemeProvider, Typography, createTheme } from "@mui/material";

import { SideBar } from "./components/SideBar";
import { GetExcelOutputNotification } from "./components/GetExcelOutputNotification";
import { UploadResult } from "./pages/UploadResult";
import { Widgets } from "./pages/Widgets";
import { ChangeLog } from "./pages/Changelog";
import { Changes } from "./utils/ChangeLogConstants";
import { PREVIOUS_RELEASE, previousReleaseLive } from "./utils/links";

const theme = createTheme({
    typography: {
        fontFamily: "Poppins",
    },
});
type SelectedMenu = "overview" | "ghg" | "jobs" | "valueAdded" | "upload";

type MenuContextType = {
    selectedMenu: SelectedMenu;
    setSelectedMenu: (s: SelectedMenu) => void;
};

const defaultSelectedMenu: SelectedMenu = "overview";

export const MenuContext = createContext<MenuContextType>({
    selectedMenu: defaultSelectedMenu,
    setSelectedMenu: (defaultSelectedMenu: SelectedMenu) => {},
});

export const Layout = () => {
    // Release version for the footer, from the backend's /api/env (shared/version.py). The changelog's top
    // entry is only the fallback while the fetch is pending or fails: it is hand-maintained and frozen between
    // member-facing releases, so a release candidate or bug-fix release never updates it.
    const [version, setVersion] = useState<string>("");
    useEffect(() => {
        fetch("/api/env")
            .then((r) => r.json())
            .then((d) => {
                if (d && typeof d.version === "string" && d.version) setVersion(d.version);
            })
            .catch(() => {});
    }, []);
    const [selectedMenu, setSelectedMenu] = useState<SelectedMenu>(defaultSelectedMenu);
    const location = useLocation();
    useEffect(() => {
        fetch("/api/auth/userinfo", {})
            .then((response) => {
                if (response.ok) {
                } else if (response.status === 401) {
                    window.location.href = "/api/auth/login";
                }
            })
            .catch(() => {});
    });

    return (
        <MenuContext.Provider value={{ selectedMenu, setSelectedMenu }}>
            <Stack position="relative" minHeight="100vh">
                {/* Sandbox warning banner is rendered in public/index.html (static, hostname-gated). */}
                <Box position="relative">
                    <Header />
                    <SideBar />
                    {<GetExcelOutputNotification />}
                    <Outlet />
                </Box>
                <Typography
                    fontSize="12px"
                    position="absolute"
                    bottom={location.pathname === "/upload" || location.pathname === "/" ? "0px" : "-100px"}
                    left="50%"
                    style={{ transform: "translate(-50%,-50%)" }}
                >
                    Joint Impact Model v. {version || Changes[0].version.replace(/^\d{4}\./, "")}
                    {process.env.REACT_APP_BUILD_DATE && ` - last update ${process.env.REACT_APP_BUILD_DATE}`}
                    {process.env.REACT_APP_BUILD_REF && <span style={{ color: "#888" }}> — Build {process.env.REACT_APP_BUILD_REF}</span>}
                    {previousReleaseLive() && (
                        <span>
                            {" "}
                            — {PREVIOUS_RELEASE.label} still available:{" "}
                            <a href={PREVIOUS_RELEASE.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
                                {PREVIOUS_RELEASE.host}
                            </a>
                        </span>
                    )}
                </Typography>
            </Stack>
        </MenuContext.Provider>
    );
};

const DataSection = () => {
    return (
        <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/ghg" element={<Ghg />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/valueadded" element={<ValueAdded />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/uploadresult" element={<UploadResult />} />
            <Route path="/widgets" element={<Widgets />} />
        </Routes>
    );
};

function App() {
    return (
        <ThemeProvider theme={theme}>
            <AlertProvider>
                <AlertPopup />
                <DataProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<Layout />}>
                                <Route index element={<Upload />} />
                                <Route path="/changelog" element={<ChangeLog />} />
                                <Route path="*" element={<DataSection />} />
                            </Route>
                        </Routes>
                    </BrowserRouter>
                </DataProvider>
            </AlertProvider>
        </ThemeProvider>
    );
}

export default App;
